import { LockIcon } from 'lucide-react';
import React, { useRef } from 'react';

import { OrganizationHeader } from '@/components/OrganizationHeader';
import type { StepProps } from '@/sections/customerManagement/components/modals/AddCustomerModal/AddCustomerModal.types';
import { EDITABLE_TYPES } from '@/sections/customerManagement/constants/addCustomerModal';
import { buildAnyTimeSentence } from '@/sections/settings/components/forms/scanScheduleSentence';
// Reuse the Settings scan-schedule presentational stack (DRY): the weekly
// frequency is DISPLAYED via the SAME summary sentence the Settings card shows.
import { SentenceText } from '@/sections/settings/components/forms/SentenceText';
import { WeekDay, WeekDayOptions } from '@/types';
import { cn } from '@/utils/classname';

/**
 * Step 4: Scan Frequency — shown for ALL customer types.
 *
 * Frequency is fixed weekly (read-only for every type) and rendered as the
 * SAME "Scan every week, any time." sentence the Settings scan-schedule card
 * shows (buildAnyTimeSentence('weekly') + SentenceText, imported as-is).
 *
 * The scan day is editable for MANAGED/SAAS/BUG_BOUNTY (single-choice
 * radiogroup mirroring the Settings weekday-cell affordance) and read-only for
 * PILOT/ENGAGEMENT/FREEMIUM (static day + Settings-style lock pill).
 */
export const ScanFrequencyStep: React.FC<StepProps> = ({
  formData,
  setFormData,
  isSubmitting,
}) => {
  const canEditScanDay =
    !!formData.customer_type &&
    (EDITABLE_TYPES as readonly string[]).includes(formData.customer_type);

  const selectedLabel = canEditScanDay
    ? (WeekDayOptions.find(o => o.value === formData.scan_day)?.label ?? 'Mon')
    : (WeekDayOptions.find(o => o.value === WeekDay.Monday)?.label ?? 'Mon');

  const cellRefs = useRef<(HTMLDivElement | null)[]>([]);

  return (
    <div className="flex flex-col gap-8">
      <OrganizationHeader
        displayName={formData.settings_display_name}
        profilePicture={formData.profile_picture}
        subtitle="Set the recurring scan cadence"
        className="mb-8"
      />

      <div className="flex flex-col gap-4">
        {/* Frequency (reused Settings summary sentence — read-only for all types) */}
        <SentenceText
          parts={buildAnyTimeSentence('weekly')}
          data-testid="scan-frequency-summary"
          className="text-chariot-text-primary text-sm"
        />

        {/* Scan day */}
        {canEditScanDay ? (
          <div
            role="radiogroup"
            aria-label="Scan day"
            className="grid grid-cols-7 gap-2"
          >
            {WeekDayOptions.map(({ label, value }, idx) => {
              const selected = value === formData.scan_day;
              const moveSelection = (delta: number) => {
                if (isSubmitting) return;
                const next =
                  (idx + delta + WeekDayOptions.length) % WeekDayOptions.length;
                setFormData(prev => ({
                  ...prev,
                  scan_day: WeekDayOptions[next].value,
                }));
                cellRefs.current[next]?.focus();
              };
              return (
                <div
                  key={label}
                  ref={el => {
                    cellRefs.current[idx] = el;
                  }}
                  role="radio"
                  aria-checked={selected}
                  aria-label={label}
                  tabIndex={selected ? 0 : -1}
                  onClick={() =>
                    !isSubmitting &&
                    setFormData(prev => ({ ...prev, scan_day: value }))
                  }
                  onKeyDown={e => {
                    if (!isSubmitting && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      setFormData(prev => ({ ...prev, scan_day: value }));
                    } else if (
                      e.key === 'ArrowRight' ||
                      e.key === 'ArrowDown'
                    ) {
                      e.preventDefault();
                      moveSelection(1);
                    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                      e.preventDefault();
                      moveSelection(-1);
                    }
                  }}
                  data-testid={`scan-frequency-day-${value}`}
                  className={cn(
                    'flex h-9 cursor-pointer items-center justify-center rounded-md border text-sm font-medium transition-colors',
                    'focus-visible:outline-chariot-brand-primary focus-visible:outline-2 focus-visible:outline-offset-2',
                    selected
                      ? 'border-chariot-brand-primary bg-chariot-brand-primary text-white'
                      : 'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:bg-chariot-background-secondary',
                    isSubmitting && 'cursor-not-allowed opacity-50'
                  )}
                >
                  {label}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-3">
              <span className="text-chariot-text-primary text-sm font-medium">
                {selectedLabel}
              </span>
              {/* Settings read-only convention (ScanScheduleCard lock pill) */}
              <span
                data-testid="scan-frequency-readonly-pill"
                className="border-chariot-stroke text-chariot-text-secondary inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold"
              >
                <LockIcon className="size-3" />
                Read-only
              </span>
            </div>
            <p className="text-chariot-text-secondary text-xs">
              Scan day is managed by Praetorian for this engagement type.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
